import { AppSettings } from '../../../services/settings/app-settings.service';
import { AuthService } from '../../../services/auth/auth.service';
import { TokenManagerService } from '../../../services/auth/token-manager.service';
import { ChangeDetectorRef, Component, HostListener, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ConfirmSignInComponent } from '../components/confirm-sign-in/confirm-sign-in.component';
import { ForgetPasswordComponent } from '../components/forget-password/forget-password.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CoreService } from '../../../services/core/core.service';
import { AuthService as CoreAuthService, User as CoreUser } from '../../../core/auth/auth.service';
import { getUserSub } from '../../../core/auth/jwt-helpers';
import { parseJwt } from '../../../core/auth/jwt-helpers';
import { environment } from '../../../../environments/environment';
import { RbacService } from '../../../services/rbac/rbac.service';

interface PreviousLoginSession {
  email: string;
  userSub?: string;
  name?: string;
  hasPasskey: boolean;
  passkeysEnrolled?: number;
  lastUsedAt: string;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: false,
})
export class LoginComponent implements OnDestroy {
  @ViewChild(ConfirmSignInComponent) confirmDialog: ConfirmSignInComponent | undefined;
  @ViewChild(ForgetPasswordComponent) forgetPasswordDialog: ForgetPasswordComponent | undefined;
  @ViewChild('passwordInput', { read: ElementRef }) passwordInputRef?: ElementRef;

  loading: boolean = false;
  showConfirmSignUp: boolean = false;
  forceChangePassword: boolean = false;
  showForgetPassword: boolean = false;
  loginForm: FormGroup<any>;
  nextStepSessionToken: any;
  mfaRequired: boolean = false;
  mfaMethod: string | null = null;
  mfaChallengeSession: string = '';
  passkeyRequired: boolean = false;
  passkeyChallengeSession: string = '';
  passkeyPublicKeyOptions: any = null;
  showPasskeyEnrollment: boolean = false;
  enrollingPasskey: boolean = false;
  passkeyRegistrationLabel: string = '';
  logoUrl: string = '';
  innervycsLogoUrl: string = 'https://innervycs.atlassian.net/s/-3ddrgv/b/8/e94525064957f450fd8d57803d490e7a/_/jira-logo-scaled.png';
  previousSessions: PreviousLoginSession[] = [];
  selectedPreviousSessionEmail: string | null = null;
  passwordLoginMode: boolean = false;
  emailStepConfirmed: boolean = false;
  checkingPasskeyStatus: boolean = false;
  emailHasPasskey: boolean = false;
  emailPasskeysEnrolled: number = 0;
  private readonly previousSessionsKey = 'mb.previousLoginAccounts';
  private readonly passkeyEnrollmentPromptKeyPrefix = 'mb.passkeyEnrollmentPromptSeen';
  private activePasskeyEnrollmentPromptKey: string | null = null;

  validationMessages = {
    email: [
      { type: 'minlength', msg: 'Debe tener mínimo de 6 caracteres de largo.' },
      { type: 'pattern', msg: 'El email no es válido.' },
    ],
    password: [],
  };

  constructor(
    public appSettings: AppSettings,
    private authService: AuthService,
    private tokenManager: TokenManagerService,
    private formBuilder: FormBuilder,
    private route: Router,
    private activatedRoute: ActivatedRoute,
    private coreApi: CoreService,
    private coreAuth: CoreAuthService,
    private rbacService: RbacService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService
  ) {
    this.appSettings.appEmpty.set(true);
    this.appSettings.appHideGlobalHeader.set(true);
    this.logoUrl = `${environment.assets?.s3BaseUrl || 'https://metabridge-assets.s3.us-east-1.amazonaws.com'}/logos/METABRIDGE-01.webp`;

    this.loginForm = this.formBuilder.group({
      email: ['', Validators.compose([Validators.required, Validators.minLength(6), Validators.pattern('^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$')])],
      password: ['', Validators.compose([Validators.required])],
      mfaCode: [''],
    });
  }

  get showPreviousSessionPicker(): boolean {
    return this.previousSessions.length > 0 && !this.passwordLoginMode && !this.passkeyRequired && !this.mfaRequired && !this.showPasskeyEnrollmentPrompt;
  }

  get showCredentialLogin(): boolean {
    return !this.showPreviousSessionPicker && !this.showPasskeyEnrollmentPrompt;
  }

  get loginTitle(): string {
    if (this.showPasskeyEnrollmentPrompt) return 'Inicie rapidamente con su cara, huella o pin';
    return this.showPreviousSessionPicker ? 'Elige una sesión previa' : 'Portal Administración';
  }

  get showLoginSubtitle(): boolean {
    // Durante el desafío MFA el propio paso ya rotula qué se pide; mantener
    // "Ingrese sus credenciales" compite con ese mensaje y confunde.
    return !this.showPreviousSessionPicker && !this.showPasskeyEnrollmentPrompt && !this.mfaRequired;
  }

  get showPasskeyEnrollmentPrompt(): boolean {
    return this.showPasskeyEnrollment;
  }

  get supportsPasskeys(): boolean {
    return this.authService.supportsPasskeys();
  }

  ngOnInit(): void {
    this.loadPreviousSessions();

    // Verificar si el usuario ya tiene una sesión activa
    this.checkExistingSession();

    // Si la sesión expiró, mostrar aviso
    try {
      this.activatedRoute.queryParamMap.subscribe((params) => {
        const reason = params.get('reason');
        if (params.get('sessionExpired') === '1' || reason === 'token_expired') {
          this.messageService.add({
            severity: 'warn',
            summary: 'Sesión finalizada',
            detail: 'Tu sesión expiró. Vuelve a iniciar sesión.',
            life: 4000,
          });
        } else if (reason === 'refresh_failed') {
          this.messageService.add({
            severity: 'error',
            summary: 'Error de sesión',
            detail: 'No se pudo renovar tu sesión. Por favor, inicia sesión nuevamente.',
            life: 4000,
          });
        }
      });
    } catch {}
  }

  /**
   * Verifica si existe una sesión activa y redirige a home si es válida
   */
  private checkExistingSession(): void {
    try {
      const accessToken = this.authService.getAccessToken();

      if (!accessToken) {
        return; // No hay token, mostrar login
      }

      // Verificar si el token no está expirado
      try {
        const payload = parseJwt(accessToken);
        const now = Math.floor(Date.now() / 1000);

        if (payload.exp > now) {
          // Hidratar sesión core para evitar llamadas sin auth al entrar directo desde login
          try {
            const idToken = this.authService.getIdToken() || '';
            const refreshToken = this.authService.getRefreshToken() || '';
            const orgId = (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('mb.currentOrgId') : null) || '';

            let userId = '';
            if (idToken) {
              userId = getUserSub(idToken);
            }

            const user: CoreUser = {
              userId,
              orgId,
              accessToken,
              idToken,
              refreshToken,
            };

            this.coreAuth.setCurrentUser(user);
            this.coreApi.initializeAvailableServices();
            this.tokenManager.startTokenRefreshMonitoring();
          } catch (hydrateError) {
            console.warn('⚠️ LoginComponent: No se pudo hidratar sesión core desde sesión existente:', hydrateError);
          }

          // Token válido, verificar si hay returnUrl
          this.activatedRoute.queryParams.subscribe((params) => {
            const returnUrl = params['returnUrl'];

            if (returnUrl) {
              console.log('LoginComponent: Sesión activa detectada. Redirigiendo a:', returnUrl);
              this.route.navigate([returnUrl]);
            } else {
              console.log('LoginComponent: Sesión activa detectada. Redirigiendo a home.');
              this.route.navigate(['home']);
            }
          });
        } else {
          // Token expirado, limpiar sesión
          console.warn('⚠️ LoginComponent: Token expirado detectado. Limpiando sesión.');
          this.authService.clearSession();
        }
      } catch (decodeError) {
        console.error('❌ LoginComponent: Error al decodificar token:', decodeError);
        this.authService.clearSession();
      }
    } catch (error) {
      console.error('❌ LoginComponent: Error al verificar sesión existente:', error);
    }
  }

  selectPreviousSession(session: PreviousLoginSession): void {
    this.startPasswordLogin(session.email, session.hasPasskey, session.passkeysEnrolled);
  }

  startPasswordLogin(email?: string, hasPasskey = false, passkeysEnrolled?: number): void {
    this.passwordLoginMode = true;
    this.selectedPreviousSessionEmail = email || null;
    this.emailStepConfirmed = !!email;
    this.emailHasPasskey = hasPasskey;
    this.emailPasskeysEnrolled = passkeysEnrolled || 0;
    this.passkeyRequired = false;
    this.passkeyChallengeSession = '';
    this.passkeyPublicKeyOptions = null;
    this.mfaRequired = false;
    this.mfaMethod = null;
    this.mfaChallengeSession = '';
    this.loginForm.patchValue({ email: email || '', password: '', mfaCode: '' });

    setTimeout(() => {
      const nativeElement = this.passwordInputRef?.nativeElement;
      const input = nativeElement?.querySelector?.('input') || nativeElement;
      input?.focus?.();
    });
  }

  clearPreviousSessionSelection(): void {
    this.passwordLoginMode = false;
    this.selectedPreviousSessionEmail = null;
    this.emailStepConfirmed = false;
    this.emailHasPasskey = false;
    this.emailPasskeysEnrolled = 0;
    this.passkeyRequired = false;
    this.passkeyChallengeSession = '';
    this.passkeyPublicKeyOptions = null;
    this.mfaRequired = false;
    this.mfaMethod = null;
    this.mfaChallengeSession = '';
    this.loginForm.patchValue({ email: '', password: '', mfaCode: '' });
  }

  async continueWithEmail(): Promise<void> {
    if (this.loading || this.checkingPasskeyStatus) return;

    const emailControl = this.loginForm.get('email');
    emailControl?.markAsTouched();

    if (emailControl?.invalid || !this.loginForm.value.email) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Correo requerido',
        detail: 'Ingresa un correo electrónico válido para continuar.',
        life: 4000,
      });
      return;
    }

    try {
      this.checkingPasskeyStatus = true;
      const email = String(this.loginForm.value.email).trim();
      this.loginForm.patchValue({ email, password: '', mfaCode: '' });
      const status = await this.authService.getPasskeyStatus(email);
      this.emailHasPasskey = status.hasPasskey;
      this.emailPasskeysEnrolled = status.passkeysEnrolled;
      this.emailStepConfirmed = true;
      this.passwordLoginMode = true;

      setTimeout(() => {
        const nativeElement = this.passwordInputRef?.nativeElement;
        const input = nativeElement?.querySelector?.('input') || nativeElement;
        input?.focus?.();
      });
    } catch {
      this.emailHasPasskey = false;
      this.emailPasskeysEnrolled = 0;
      this.emailStepConfirmed = true;
      this.passwordLoginMode = true;
    } finally {
      this.checkingPasskeyStatus = false;
    }
  }

  get isMfaCodeComplete(): boolean {
    return /^\d{6}$/.test(String(this.loginForm.value.mfaCode ?? ''));
  }

  /** Descarta el desafío MFA y devuelve al usuario al paso de correo. */
  cancelMfaChallenge(): void {
    if (this.loading) return;

    this.mfaRequired = false;
    this.mfaMethod = null;
    this.mfaChallengeSession = '';
    this.emailStepConfirmed = false;
    this.passwordLoginMode = true;
    this.loginForm.patchValue({ password: '', mfaCode: '' });
  }

  changeEmail(): void {
    if (this.loading || this.mfaRequired || this.passkeyRequired) return;

    this.emailStepConfirmed = false;
    this.emailHasPasskey = false;
    this.emailPasskeysEnrolled = 0;
    this.selectedPreviousSessionEmail = null;
    this.mfaRequired = false;
    this.mfaMethod = null;
    this.mfaChallengeSession = '';
    this.loginForm.patchValue({ password: '', mfaCode: '' });
  }

  removePreviousSession(event: Event, session: PreviousLoginSession): void {
    event.preventDefault();
    event.stopPropagation();

    try {
      this.previousSessions = this.previousSessions.filter((item) => item.email.toLowerCase() !== session.email.toLowerCase());
      this.resetPasskeyEnrollmentPromptForSession(session);

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(this.previousSessionsKey, JSON.stringify(this.previousSessions));
      }

      if (this.selectedPreviousSessionEmail?.toLowerCase() === session.email.toLowerCase()) {
        this.clearPreviousSessionSelection();
      }

      if (this.previousSessions.length === 0) {
        this.passwordLoginMode = true;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Sesión local eliminada',
        detail: 'Esta cuenta ya no quedará recordada en este equipo.',
        life: 3500,
      });
    } catch (error) {
      console.warn('⚠️ LoginComponent: No se pudo eliminar la sesión previa:', error);
      this.messageService.add({
        severity: 'warn',
        summary: 'No se pudo eliminar',
        detail: 'Intenta nuevamente en unos segundos.',
        life: 4000,
      });
    }
  }

  async loginWithPreviousPasskey(session: PreviousLoginSession): Promise<void> {
    if (this.loading) return;

    if (!this.authService.supportsPasskeys()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Passkey no disponible',
        detail: 'Este navegador o dispositivo no soporta passkeys. Ingresa tu contraseña para continuar.',
        life: 5000,
      });
      this.selectPreviousSession(session);
      return;
    }

    try {
      this.loading = true;
      this.selectedPreviousSessionEmail = session.email;
      const data = await this.authService.signInWithPasskey(session.email);
      await this.completeSuccessfulLogin(data);
    } catch (error: any) {
      const status = error?.response?.status ?? error?.status;
      const detail = error?.response?.data?.message || error?.message || '';
      const shouldFallbackToPassword = status === 400 || /passkey/i.test(detail);

      if (shouldFallbackToPassword) {
        this.updateRememberedSessionPasskey(session.email, false, 0);
        this.selectPreviousSession({ ...session, hasPasskey: false, passkeysEnrolled: 0 });
      }

      this.messageService.add({
        severity: shouldFallbackToPassword ? 'warn' : 'error',
        summary: 'No se pudo usar passkey',
        detail: shouldFallbackToPassword
          ? 'No encontramos un passkey vigente para esta cuenta. Ingresa tu contraseña para continuar.'
          : 'No se pudo completar la verificación con passkey. Intenta nuevamente o usa contraseña.',
        life: 5500,
      });
    } finally {
      this.loading = false;
    }
  }

  async loginWithPasskeyFromForm(): Promise<void> {
    if (this.loading) return;

    const emailControl = this.loginForm.get('email');
    emailControl?.markAsTouched();

    if (emailControl?.invalid || !this.loginForm.value.email) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Correo requerido',
        detail: 'Ingresa tu correo electrónico para solicitar tu passkey.',
        life: 4500,
      });
      return;
    }

    if (!this.authService.supportsPasskeys()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Passkey no disponible',
        detail: 'Este navegador o dispositivo no soporta passkeys. Ingresa tu contraseña para continuar.',
        life: 5000,
      });
      return;
    }

    try {
      this.loading = true;
      const data = await this.authService.signInWithPasskey(this.loginForm.value.email);
      await this.completeSuccessfulLogin(data);
    } catch (error: any) {
      const status = error?.response?.status ?? error?.status;
      const detail =
        status === 400
          ? 'Esta cuenta no tiene passkeys disponibles. Ingresa tu contraseña para continuar.'
          : 'No se pudo completar la verificación con passkey. Intenta nuevamente o usa contraseña.';

      if (status === 400) {
        this.emailHasPasskey = false;
        this.emailPasskeysEnrolled = 0;
      }

      this.messageService.add({
        severity: status === 400 ? 'warn' : 'error',
        summary: 'No se pudo usar passkey',
        detail,
        life: 5500,
      });
    } finally {
      this.loading = false;
    }
  }

  private loadPreviousSessions(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const raw = localStorage.getItem(this.previousSessionsKey);
      const parsed = raw ? JSON.parse(raw) : [];

      this.previousSessions = Array.isArray(parsed)
        ? parsed
            .filter((item: any) => typeof item?.email === 'string' && item.email.includes('@'))
            .map((item: any) => ({
              email: item.email,
              userSub: typeof item.userSub === 'string' ? item.userSub : undefined,
              name: typeof item.name === 'string' ? item.name : undefined,
              hasPasskey: item.hasPasskey === true,
              passkeysEnrolled: typeof item.passkeysEnrolled === 'number' ? item.passkeysEnrolled : undefined,
              lastUsedAt: typeof item.lastUsedAt === 'string' ? item.lastUsedAt : new Date().toISOString(),
            }))
            .sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt))
            .slice(0, 5)
        : [];
    } catch (error) {
      console.warn('⚠️ LoginComponent: No se pudieron cargar sesiones previas:', error);
      this.previousSessions = [];
    }
  }

  private rememberPreviousSession(data: any): void {
    try {
      if (typeof localStorage === 'undefined') return;

      const identity = this.extractLoginIdentity(data);
      const email = identity.email || this.loginForm.value.email;
      if (!email) return;

      const existing = this.previousSessions.find((session) => session.email.toLowerCase() === email.toLowerCase());
      const passkeysEnrolled = typeof data?.passkeysEnrolled === 'number' ? data.passkeysEnrolled : existing?.passkeysEnrolled;
      const hasPasskey =
        typeof passkeysEnrolled === 'number'
          ? passkeysEnrolled > 0
          : data?.passkeyVerified === true || data?.authMethod === 'passkey' || existing?.hasPasskey === true;

      const updatedSession: PreviousLoginSession = {
        email,
        userSub: identity.userSub || existing?.userSub,
        name: identity.name || existing?.name,
        hasPasskey,
        passkeysEnrolled,
        lastUsedAt: new Date().toISOString(),
      };

      this.previousSessions = [updatedSession, ...this.previousSessions.filter((session) => session.email.toLowerCase() !== email.toLowerCase())]
        .sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt))
        .slice(0, 5);

      localStorage.setItem(this.previousSessionsKey, JSON.stringify(this.previousSessions));
    } catch (error) {
      console.warn('⚠️ LoginComponent: No se pudo recordar la sesión previa:', error);
    }
  }

  private updateRememberedSessionPasskey(email: string | null | undefined, hasPasskey: boolean, passkeysEnrolled?: number): void {
    if (!email) return;

    try {
      if (typeof localStorage === 'undefined') return;
      this.previousSessions = this.previousSessions.map((session) =>
        session.email.toLowerCase() === email.toLowerCase()
          ? {
              ...session,
              hasPasskey,
              passkeysEnrolled,
              lastUsedAt: new Date().toISOString(),
            }
          : session
      );
      localStorage.setItem(this.previousSessionsKey, JSON.stringify(this.previousSessions));
    } catch (error) {
      console.warn('⚠️ LoginComponent: No se pudo actualizar la sesión previa:', error);
    }
  }

  private extractLoginIdentity(data: any): { email?: string; name?: string; userSub?: string } {
    const candidateTokens = [data?.idToken, this.authService.getIdToken(), data?.accessToken, this.authService.getAccessToken()].filter(Boolean);

    for (const token of candidateTokens) {
      try {
        const payload = parseJwt(token);
        return {
          email: typeof payload['email'] === 'string' ? payload['email'] : undefined,
          name: typeof payload['name'] === 'string' ? payload['name'] : undefined,
          userSub: typeof payload.sub === 'string' ? payload.sub : undefined,
        };
      } catch {}
    }

    return {};
  }

  ngOnDestroy() {
    // No establecer appEmpty aquí, dejar que el componente destino lo maneje
    // this.appSettings.appEmpty.set(false);
    // this.appSettings.appHideGlobalHeader.set(false);
  }

  async onLogin(username?: string, password?: string): Promise<any> {
    if (!this.emailStepConfirmed && !username) {
      await this.continueWithEmail();
      return;
    }

    try {
      this.loading = true;

      const data = await this.authService.signIn(
        username ?? this.loginForm.value.email,
        password ?? this.loginForm.value.password,
        this.mfaRequired ? this.loginForm.value.mfaCode : undefined,
        this.mfaRequired ? this.mfaChallengeSession : undefined
      );

      const nextStep = data?.nextStep;
      console.log('nextStep', nextStep);

      if (nextStep?.signInStep === 'PASSKEY_REQUIRED') {
        this.passkeyRequired = true;
        this.passkeyChallengeSession = nextStep.session;
        this.passkeyPublicKeyOptions = nextStep.publicKeyOptions;
        this.mfaRequired = false;
        this.mfaMethod = null;
        this.messageService.add({
          severity: 'info',
          summary: 'Verificación adicional',
          detail: 'Confirma tu identidad con tu passkey para completar el inicio de sesión.',
          life: 4500,
        });
        return;
      }

      if (!nextStep || !nextStep.signInStep) {
        await this.completeSuccessfulLogin(data);
        return;
      }

      switch (nextStep.signInStep) {
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        case 'NEW_PASSWORD_REQUIRED':
          this.showConfirmSignUp = true;
          this.nextStepSessionToken = nextStep.session;
          break;

        case 'SOFTWARE_TOKEN_MFA':
        case 'SMS_MFA':
          this.mfaRequired = true;
          this.mfaMethod = nextStep.signInStep;
          this.mfaChallengeSession = nextStep.session || '';
          break;

        case 'DONE':
          await this.completeSuccessfulLogin(data);
          break;

        default:
          console.log(nextStep);
          break;
      }
    } catch (error: any) {
      // Adaptar al shape de error de Axios: error.response.{status,data}
      const status = error?.response?.status ?? error?.status;
      const data = error?.response?.data ?? error?.error;
      const code = data?.code;
      let message = 'Ha ocurrido un error inesperado. Intente de nuevo.';

      if (code === 'USER_NOT_FOUND' || status === 404) {
        message = 'La cuenta no existe. Verifique el correo electrónico o contacte a un administrador.';
      } else if (code === 'INVALID_CREDENTIALS' || status === 401) {
        message = 'Credenciales incorrectas. Intente de nuevo.';
      } else if (code === 'USER_DISABLED') {
        message = 'La cuenta está deshabilitada. Contacte al administrador.';
      } else if (code === 'USER_NOT_CONFIRMED') {
        message = 'La cuenta aún no ha sido confirmada. Revise su correo electrónico.';
      } else if (code === 'PASSWORD_RESET_REQUIRED') {
        message = 'Se requiere restablecer la contraseña. Verifique su correo electrónico.';
      } else if (code === 'TOO_MANY_ATTEMPTS' || status === 429) {
        message = 'Demasiados intentos fallidos. Intente más tarde.';
      } else if (status === 403) {
        message = data?.message ?? 'Acceso denegado. La suscripción de la organización puede estar inactiva.';
      } else if (status === 400) {
        message = data?.message ?? 'Petición inválida.';
      } else if (error?.message?.toLowerCase?.().includes('network')) {
        message = 'Error de red. Verifique su conexión.';
      }

      const shouldResetMfaChallenge =
        this.mfaRequired && (status === 400 || status === 401 || code === 'MFA_SESSION_REQUIRED' || code === 'INVALID_CREDENTIALS');

      if (shouldResetMfaChallenge) {
        // Cognito invalida la sesión del challenge tras un intento fallido:
        // hay que reautenticar con contraseña para obtener una nueva.
        message = 'El código de verificación no es válido. Ingresa tu contraseña nuevamente para reintentar.';
        this.mfaRequired = false;
        this.mfaMethod = null;
        this.mfaChallengeSession = '';
        this.loginForm.patchValue({ password: '', mfaCode: '' });
      }

      this.messageService.add({
        severity: 'error',
        summary: 'Error de autenticación',
        detail: message,
        life: 5000,
      });
    } finally {
      this.loading = false;
    }
  }

  async completePasskeyChallenge(): Promise<void> {
    if (!this.passkeyChallengeSession || !this.passkeyPublicKeyOptions) {
      this.messageService.add({
        severity: 'error',
        summary: 'Passkey no disponible',
        detail: 'No hay un desafío de passkey pendiente. Vuelve a iniciar sesión.',
        life: 5000,
      });
      return;
    }

    try {
      this.loading = true;
      const data = await this.authService.completePasskeySignIn(this.passkeyChallengeSession, this.passkeyPublicKeyOptions);
      this.passkeyRequired = false;
      this.passkeyChallengeSession = '';
      this.passkeyPublicKeyOptions = null;
      await this.completeSuccessfulLogin(data);
    } catch (error: any) {
      const message =
        error?.message === 'PASSKEY_NOT_SUPPORTED'
          ? 'Este navegador o dispositivo no soporta passkeys.'
          : 'No se pudo verificar el passkey. Intente nuevamente.';
      this.messageService.add({
        severity: 'error',
        summary: 'Error con passkey',
        detail: message,
        life: 5000,
      });
    } finally {
      this.loading = false;
    }
  }

  cancelPasskeyChallenge(): void {
    this.passkeyRequired = false;
    this.passkeyChallengeSession = '';
    this.passkeyPublicKeyOptions = null;
    this.loginForm.patchValue({ password: '', mfaCode: '' });
  }

  async enrollPasskeyAfterLogin(): Promise<void> {
    try {
      this.enrollingPasskey = true;
      const result = await this.authService.registerPasskey(this.passkeyRegistrationLabel || 'Dispositivo principal');
      const passkeysEnrolled = result?.passkeys?.length ?? 1;
      this.updateRememberedSessionPasskey(this.authService.getEmail() || this.loginForm.value.email, passkeysEnrolled > 0, passkeysEnrolled);
      this.messageService.add({
        severity: 'success',
        summary: 'Passkey configurado',
        detail: 'Tu cuenta ahora usará passkey como segundo factor en futuros inicios de sesión.',
        life: 5000,
      });
      this.showPasskeyEnrollment = false;
      this.route.navigate(['home']);
    } catch (error: any) {
      const message =
        error?.message === 'PASSKEY_NOT_SUPPORTED'
          ? 'Este navegador o dispositivo no soporta passkeys.'
          : 'No se pudo registrar el passkey. Puedes intentarlo luego desde Mi perfil.';
      this.messageService.add({
        severity: 'warn',
        summary: 'Passkey pendiente',
        detail: message,
        life: 6000,
      });
    } finally {
      this.enrollingPasskey = false;
    }
  }

  skipPasskeyEnrollment(): void {
    this.markPasskeyEnrollmentPromptSeen();
    this.showPasskeyEnrollment = false;
    this.route.navigate(['home']);
  }

  private async completeSuccessfulLogin(data: any): Promise<void> {
    this.mfaRequired = false;
    this.mfaMethod = null;
    this.mfaChallengeSession = '';
    this.passkeyRequired = false;

    const session = this.hydrateCoreAuthFromStoredTokens(data);

    if (session.userId && session.accessToken) {
      const hasAdminAccess = await this.rbacService.checkPlatformAdminAccess(session.userId, session.accessToken);

      if (!hasAdminAccess) {
        this.messageService.add({
          severity: 'error',
          summary: 'Acceso Denegado',
          detail: 'No tienes permisos de administrador de plataforma. Este panel es solo para administradores del sistema Metabridge.',
          life: 6000,
        });

        this.authService.clearSession();
        this.loading = false;
        return;
      }
    }

    this.rememberPreviousSession(data);

    this.coreApi.initializeAvailableServices();
    this.tokenManager.startTokenRefreshMonitoring();

    if (data?.passkeyEnrollmentRequired && this.authService.supportsPasskeys() && !this.hasSeenPasskeyEnrollmentPrompt(data)) {
      this.passkeyRegistrationLabel = this.defaultPasskeyLabel();
      this.activePasskeyEnrollmentPromptKey = this.getPasskeyEnrollmentPromptKey(data);
      this.markPasskeyEnrollmentPromptSeen();
      this.showPasskeyEnrollment = true;
      return;
    }

    this.route.navigate(['home']);
  }

  private hydrateCoreAuthFromStoredTokens(data: any): { userId: string; accessToken: string } {
    let userId = '';
    let accessToken = '';
    try {
      accessToken = (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('mb.accessToken') : null) || data?.accessToken || '';
      const idToken = (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('mb.idToken') : null) || data?.idToken || '';
      const refreshToken = (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('mb.refreshToken') : null) || data?.refreshToken || '';
      const orgId = (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('mb.currentOrgId') : null) || data?.orgId || '';
      try {
        if (idToken) userId = getUserSub(idToken);
      } catch {}
      const user: CoreUser = { userId, orgId, accessToken, idToken, refreshToken };
      this.coreAuth.setCurrentUser(user);
    } catch {}

    return { userId, accessToken };
  }

  private defaultPasskeyLabel(): string {
    try {
      const platform = navigator.platform || 'dispositivo';
      return `Passkey de ${platform}`;
    } catch {
      return 'Dispositivo principal';
    }
  }

  private hasSeenPasskeyEnrollmentPrompt(data?: any): boolean {
    try {
      if (typeof localStorage === 'undefined') return false;
      const key = this.getPasskeyEnrollmentPromptKey(data);
      return key ? localStorage.getItem(key) === '1' : false;
    } catch {
      return false;
    }
  }

  private markPasskeyEnrollmentPromptSeen(): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const key = this.activePasskeyEnrollmentPromptKey || this.getPasskeyEnrollmentPromptKey();
      if (key) localStorage.setItem(key, '1');
    } catch {}
  }

  private getPasskeyEnrollmentPromptKey(data?: any): string | null {
    const identity = data ? this.extractLoginIdentity(data) : this.extractLoginIdentity({});
    const identifier = identity.userSub || identity.email || this.authService.getEmail() || this.loginForm.value.email;
    if (!identifier) return null;
    return this.getPasskeyEnrollmentPromptKeyForIdentifier(identifier);
  }

  private getPasskeyEnrollmentPromptKeyForIdentifier(identifier: string): string {
    return `${this.passkeyEnrollmentPromptKeyPrefix}.${encodeURIComponent(String(identifier).toLowerCase())}`;
  }

  private resetPasskeyEnrollmentPromptForSession(session: PreviousLoginSession): void {
    try {
      if (typeof localStorage === 'undefined') return;
      const identifiers = [session.email, session.userSub].filter((value): value is string => typeof value === 'string' && value.length > 0);
      identifiers.forEach((identifier) => localStorage.removeItem(this.getPasskeyEnrollmentPromptKeyForIdentifier(identifier)));
    } catch {}
  }

  openForgetPasswordDialog(): void {
    this.showForgetPassword = true;
    this.cdr.detectChanges();
    console.log('Abriendo diálogo de recuperación de contraseña...');
  }

  closeForgetPasswordDialog(): void {
    this.showForgetPassword = false;
    this.cdr.detectChanges();
  }

  onRegister() {
    console.log('Navegando a la página de registro...');
    // Aquí iría la lógica para navegar a la página de registro
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Verificar si se presionó Enter
    if (event.key === 'Enter' || event.keyCode === 13) {
      const activeElement = document.activeElement;

      // Verificar si el elemento activo es un input dentro de p-password
      const isPasswordInput =
        activeElement?.tagName === 'INPUT' &&
        ((activeElement as HTMLInputElement).type === 'password' ||
          activeElement?.closest('p-password') !== null ||
          activeElement?.getAttribute('formcontrolname') === 'password');

      // Verificar si es el campo MFA
      const isMfaField = activeElement?.id === 'mfaCode' || activeElement?.getAttribute('formcontrolname') === 'mfaCode';

      // Verificar si es el campo de email (para que también funcione desde ahí)
      const isEmailField = activeElement?.id === 'username' || activeElement?.getAttribute('formcontrolname') === 'email';

      // Si estamos en cualquier campo del formulario y no está cargando
      if ((isPasswordInput || isMfaField || isEmailField) && !this.loading) {
        if (isEmailField && !this.emailStepConfirmed) {
          event.preventDefault();
          event.stopPropagation();
          void this.continueWithEmail();
          return;
        }

        // Si no requiere MFA y el formulario es válido
        if (!this.mfaRequired && this.loginForm.valid) {
          event.preventDefault();
          event.stopPropagation();
          this.onLogin();
        }
        // Si requiere MFA y tiene código
        else if (this.mfaRequired && this.loginForm.value.mfaCode) {
          event.preventDefault();
          event.stopPropagation();
          this.onLogin();
        }
      }
    }
  }

  /** Sesiones previas guardadas: se identifican por el correo de la cuenta. */
  trackBySessionKey(_index: number, session: { email?: string; username?: string }): string {
    return session.email || session.username || '';
  }
}
